<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "events:entryAction.setNotesForEntry.heading",
                "Add note for a horse / entry"
              )
            }}
          </div>
          <p>
            {{
              $t(
                "events:entryAction.setNotesForEntry.explanation.text",
                "You are going to add or change a note."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            :disabled="formSubmitted"
            class="mx-0"
            variant="text"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="() => (dialogValue = false)"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseType == 'error'">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t(
                "events:entryAction.setNotesForEntry.competition.label",
                "Entry"
              )
            }}
          </template>
          <template #athleteNamesHeader>
            {{
              $t("events:entryAction.setNotesForEntry.athlete.label", "Athlete")
            }}
          </template>
          <template #horseNamesHeader>
            {{
              $t("events:entryAction.setNotesForEntry.horseName.label", "Horse")
            }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <div v-if="entry.registrantComment" data-test="registrantComment">
          <div class="mb-2">
            <h1 class="text-h6 font-weight-bold">
              {{
                $t(
                  "events:entryAction.setNotesForEntry.athleteNote.heading",
                  "Athlete´s note"
                )
              }}
            </h1>
            <small>{{
              $t(
                "events:entryAction.setNotesForEntry.athleteNote.hint",
                "This note was added by athlete / registrant person, when doing an entry."
              )
            }}</small>
          </div>
          <p>
            {{ entry.registrantComment }}
          </p>
        </div>

        <template v-if="initFormLoading">
          <v-progress-linear indeterminate />
          <br />
        </template>

        <div v-if="v$.form.noteToFeiOrganiser">
          <div>
            <h1 class="text-h6 font-weight-bold">
              {{
                $t(
                  "events:entryAction.setNotesForEntry.FEIOCnote.heading",
                  "Note on entry to be sent to OC (FEI events)"
                )
              }}
            </h1>
            <small>{{
              $t(
                "This note will be added to entry after entry is submitted for approval to OC via FEI Entry System"
              )
            }}</small>
          </div>
          <br />
          <v-textarea
            v-model="form.noteToFeiOrganiser"
            :label="
              $t(
                'events:entryAction.setNotesForEntry.FEIOCnoteAddNote.label',
                'Add note'
              )
            "
            :hint="
              $t(
                'events:entryAction.setNotesForEntry.FEIOCnoteAddNote.hint',
                'Add or change note. If changed, previous note will not be saved and new / changed note will be added.'
              )
            "
            data-test="noteToFeiOrganiser"
            variant="outlined"
            :disabled="fieldDisabled('noteToFeiOrganiser')"
            :error-messages="fieldErrorMessages('noteToFeiOrganiser')"
            @blur="() => v$.form.noteToFeiOrganiser.$touch()"
          />
        </div>

        <div v-if="v$.form.internalNote">
          <div>
            <h1 class="text-h6 font-weight-bold">
              {{
                $t(
                  "events:entryAction.setNotesForEntry.internalNote.heading",
                  "Internal note"
                )
              }}
            </h1>
            <small>{{
              $t(
                "events:entryAction.setNotesForEntry.internalNote.hint",
                "This note will be saved for internal use in the system, will not be exported to the third parties. Note is not visible to athlete."
              )
            }}</small>
          </div>
          <br />
          <v-textarea
            v-model="form.internalNote"
            :label="
              $t(
                'events:entryAction.setNotesForEntry.internalNote.label',
                'Add internal note'
              )
            "
            :hint="
              $t(
                'events:entryAction.setNotesForEntry.internalNote.hint',
                'Add or change note. If changed, previous note will not be saved and new / changed note will be added.'
              )
            "
            data-test="internalNote"
            variant="outlined"
            :disabled="fieldDisabled('internalNote')"
            :error-messages="fieldErrorMessages('internalNote')"
            @blur="() => v$.form.internalNote.$touch()"
          />
        </div>

        <template v-if="entry.connectedWithFei">
          <div class="mb-2">
            <b>{{
              $t(
                "events:entryAction.setNotesForEntry.alertNote.heading",
                "lease read before submitting:"
              )
            }}</b>
          </div>

          <AppAlertNotice center>
            {{
              $t(
                "events:entryAction.setNotesForEntry.alertNoteFei.text",
                "The note that is meant to the OC of FEI events will be added to entry in the FEI entry system only after the entry is submitted for approval by OC. Note will be changed and synced with FEI System, when entry is in waiting, accepted or withdrawn statuses. It will be visible both for NF and OC. "
              )
            }}
          </AppAlertNotice>
        </template>
      </div>
    </template>

    <template #submit>
      {{
        $t("events:entryAction.setNotesForEntry.setNote.button", "Set notes")
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="() => (dialogValue = false)"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useFormValidation } from "@/composables/form/form_validation";
import { useEntryFeiActionsInjections } from "./compositions";
import { DisplayTextFragments } from "@composables/display_text";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";

import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";

import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";
import { useQuery } from "@vue/apollo-composable";

defineOptions({
  name: "EntryFeiNotes",
  fragments: {
    registrationFields: gql`
      fragment _ on Registration {
        id
        eventId
      }
    `,
    eventFields: gql`
      fragment _ on Event {
        id
        ${AppShowEventDetails.fragments.event}
      }
    `,
    competitionFields: gql`
      fragment _ on Competition {
        id
        ${AppEntryRegistrationOverview.fragments.competitionFields}
      }
    `,
    entryFields: gql`
      fragment _ on Entry {
        id
      }
    `
  }
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();
const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const form = ref({
  entryId: null,
  noteToFeiOrganiser: null,
  internalNote: null
});
const modal = ref(false);
const formSubmitted = ref(false);
const responseType = ref(null);
const mutationErrors = ref([]);
const formError = ref({});
const initEntryUpdateNotesForm = ref({
  vuelidateValidations: {},
  readOnlyFields: []
});

const {
  v$,
  fieldDisabled,
  fieldErrorMessages,
  formValidate,
  formErrorMessages
} = useFormValidation(initEntryUpdateNotesForm, form);

const entryLoading = ref(false);
const entry = ref(null);

const registration = computed(() => {
  return entry.value?.registration;
});
const event = computed(() => {
  return entry.value?.registration?.event;
});
const competition = computed(() => {
  return entry.value?.competition;
});

const dialogValue = computed({
  get() {
    return modal.value;
  },
  set(v) {
    if (v == true && !modal.value) {
      responseType.value = null;
      mutationErrors.value = [];
    }
    modal.value = v;
  }
});

const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:entryAction.setNotesForEntry.success.heading",
          "Set notes for entry"
        ),
        main: t(
          "events:entryAction.setNotesForEntry.success.mainMessage.text",
          "Notes added or edited"
        ),
        okBtn: t("events:entryAction.setNotesForEntry.success.ok.button", "OK")
      };
    case "error":
      return {
        headline: t(
          "events:entryAction.setNotesForEntry.failed.heading",
          "Set notes for entry"
        ),
        main: t(
          "events:entryAction.setNotesForEntry.failed.mainMessage.text",
          "Adding or changing notes failed"
        ),
        reason: t(
          "events:entryAction.setNotesForEntry.failed.reason.text",
          "The system reported the following reason:"
        ),
        additional: t(
          "events:entryAction.setNotesForEntry.failed.additionalMessage.text",
          "Please contact your National Federation, if you need help on this matter."
        ),
        okBtn: t("events:entryAction.setNotesForEntry.failed.ok.button", "OK")
      };
    default:
      return null;
  }
});

const { onResult, loading: initFormLoading } = useQuery(
  gql`
    query initEntryUpdateNotesForm($entryId: ID) {
      initEntryUpdateNotesForm(entryId: $entryId) {
        vuelidateValidations
        readOnlyFields
      }
    }
  `,
  () => ({
    entryId: entry.value.id
  }),
  () => ({
    enabled: dialogValue.value == true && !entryLoading.value
  })
);

onResult(({ loading, data }) => {
  if (loading) return;
  initEntryUpdateNotesForm.value = data.initEntryUpdateNotesForm;
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;
  try {
    const { data } = await apolloClient.query({
      query: gql`
        query EntryFeiNotesEntryFetch($id: ID!) {
          entry(id: $id) {
            id
            connectedWithFei
            registrantComment
            noteToFeiOrganiser
            internalNote
            allowedMutations
            registration {
              id
            
              athletes {
                id
                name
                ${AppEntryRegistrationOverview.fragments.personFields}
              }
              horses {
                id
                name
                ${AppEntryRegistrationOverview.fragments.horseFields}
              }
              eventId
              event {
                id
                ${AppShowEventDetails.fragments.event}
              }
            }
            competition {
              id
              ${AppEntryRegistrationOverview.fragments.competitionFields}
            }
          }
        }
      `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;

    form.value = {
      entryId: data.entry.id,
      noteToFeiOrganiser: data.entry.noteToFeiOrganiser,
      internalNote: data.entry.internalNote
    };
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

async function submit() {
  formError.value = null;
  if (!formValidate()) {
    formError.value = formErrorMessages();
    return;
  }

  formSubmitted.value = true;

  try {
    const { data, errors } = await apolloClient.mutate({
      mutation: gql`
        mutation entryUpdateNotes(
          $entryId: ID!
          $noteToFeiOrganiser: String
          $internalNote: String
        ) {
          entryUpdateNotes(
            input: {
              entryId: $entryId
              noteToFeiOrganiser: $noteToFeiOrganiser
              internalNote: $internalNote
            }
          ) {
            feedbackMessages {
              ${DisplayTextFragments.translation}
            }
            entry {
              id
              status
              resolution
              registrantComment
              noteToFeiOrganiser
              internalNote
              connectedWithFei
              allowedMutations
            }
          }
        }
      `,
      variables: {
        entryId: entry.value.id,
        noteToFeiOrganiser: form.value.noteToFeiOrganiser,
        internalNote: form.value.internalNote
      }
    });

    if (data?.entryUpdateNotes) {
      responseType.value = "success";
    } else if (errors && errors.length > 0) {
      responseType.value = "error";
      mutationErrors.value = errors;
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}

defineExpose({ openFeiActionForm });
</script>
